import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Key, Power, PowerOff, FileSpreadsheet } from 'lucide-react';

export default function AgencyManagement() {
  const { getAuthHeaders, user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [exporting, setExporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: ''
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/agencies`, {
        headers: getAuthHeaders()
      });
      setAgencies(response.data);
    } catch (error) {
      toast.error('Failed to fetch agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAgencies = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/export/agencies`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `agency_details_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Agency data exported successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export agency data');
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAgency) {
        await axios.put(`${API_URL}/agencies/${editingAgency.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Agency updated successfully');
      } else {
        await axios.post(`${API_URL}/agencies`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Agency created successfully. Default password: password123');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchAgencies();
    } catch (error) {
      toast.error('Failed to save agency');
    }
  };

  const handleEdit = (agency) => {
    setEditingAgency(agency);
    setFormData({
      name: agency.name,
      contact_person: agency.contact_person,
      email: agency.email,
      phone: agency.phone
    });
    setIsDialogOpen(true);
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/agencies/${selectedAgency.id}/reset-password`,
        null,
        {
          headers: getAuthHeaders(),
          params: { new_password: newPassword }
        }
      );
      toast.success('Password reset successfully');
      setIsPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedAgency(null);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleToggleStatus = async (agencyId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this agency?`)) return;

    try {
      await axios.put(`${API_URL}/agencies/${agencyId}/toggle-status`, null, {
        headers: getAuthHeaders()
      });
      toast.success(`Agency ${action}d successfully`);
      fetchAgencies();
    } catch (error) {
      toast.error(`Failed to ${action} agency`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: ''
    });
    setEditingAgency(null);
  };

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {user?.role === 'master_agency' ? 'My Agencies' : 'Agency Management'}
            </h1>
            <p className="text-slate-600 mt-2">Manage admission agencies</p>
          </div>
          <div className="flex gap-3">
            {(user?.role === 'admin' || user?.role === 'sub_admin') && (
              <Button 
                onClick={handleExportAgencies} 
                disabled={exporting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="export-agencies-button"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Excel'}
              </Button>
            )}
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-agency-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Agency
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingAgency ? 'Edit Agency' : 'Add New Agency'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Agency Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="agency-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    required
                    data-testid="agency-contact-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email (Will be used as username)</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="agency-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    data-testid="agency-phone-input"
                  />
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                  Note: Default password will be set to <strong>password123</strong>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="agency-submit-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                    Create Agency
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading agencies...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <table className="w-full" data-testid="agencies-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Name</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Contact Person</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Email</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Phone</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {agencies.map((agency) => (
                  <tr key={agency.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`agency-row-${agency.id}`}>
                    <td className="p-4 align-middle text-sm text-slate-700 font-medium">{agency.name}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{agency.contact_person}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{agency.email}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{agency.phone}</td>
                    <td className="p-4 align-middle">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agency.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {agency.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(agency)}
                          data-testid={`edit-agency-${agency.id}`}
                          title="Edit Agency"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedAgency(agency);
                            setIsPasswordDialogOpen(true);
                          }}
                          data-testid={`reset-password-${agency.id}`}
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4 text-amber-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(agency.id, agency.is_active !== false)}
                          data-testid={`toggle-status-${agency.id}`}
                          title={agency.is_active !== false ? 'Deactivate' : 'Activate'}
                        >
                          {agency.is_active !== false ? (
                            <PowerOff className="w-4 h-4 text-red-600" />
                          ) : (
                            <Power className="w-4 h-4 text-green-600" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {agencies.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No agencies found. Add your first agency to get started.
              </div>
            )}
          </div>
        )}

        {/* Password Reset Dialog */}
        <Dialog open={isPasswordDialogOpen} onOpenChange={(open) => {
          setIsPasswordDialogOpen(open);
          if (!open) {
            setNewPassword('');
            setSelectedAgency(null);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password for {selectedAgency?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsPasswordDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handlePasswordReset} data-testid="reset-password-submit" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                  Reset Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
}